import React, { useState } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import axios from 'axios';
import { useGeneratorContext } from '../../context/GeneratorContext';
import { useAuth } from '../../context/AuthProvider';
import { ThinkhausHeaderTitle } from '../Generator/ThinkhausHeaderTitle';

interface GeneratedPost {
    id: number;
    content: string;
    loading: boolean;
}

export const SocialMediaPostGenerator: React.FC = () => {
    const URL = process.env.REACT_APP_THINKHAUS_URL
    const { session } = useAuth();
    const [prompt, setPrompt] = useState('');
    const [numberOfPosts, setNumberOfPosts] = useState<number>(1);
    const [generatedPosts, setGeneratedPosts] = useState<GeneratedPost[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectedModel, setSelectedModel] = useState('openai');
    const toastRef = React.useRef<Toast>(null);

    const modelOptions = [
        { label: 'GPT (OpenAI)', value: 'openai' },
        { label: 'Grok', value: 'grok' }
    ];

    const handleSubmit = async () => {
        if (!prompt.trim()) {
            toastRef.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please enter a prompt',
                life: 3000
            });
            return;
        }

        if (!numberOfPosts || numberOfPosts < 1) {
            toastRef.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please enter a valid number of posts',
                life: 3000
            });
            return;
        }

        setIsGenerating(true);
        
        // Initialize posts with loading state
        const initialPosts: GeneratedPost[] = Array.from({ length: numberOfPosts }, (_, i) => ({
            id: Date.now() + i,
            content: '',
            loading: true
        }));
        setGeneratedPosts(initialPosts);

        try {
            const config = {
                headers: {
                    "Authorization": `Bearer ${session?.access_token}`,
                }
            };

            // Determine the endpoint based on selected model
            const endpoint = `api/generate/${selectedModel}`; 

            // Make all requests in parallel
            const requests = Array.from({ length: numberOfPosts }, () =>
                axios.post(URL + endpoint, { prompt }, config)
            );

            const responses = await Promise.allSettled(requests);

            // Update posts with results
            const updatedPosts = responses.map((response, index) => {
                if (response.status === 'fulfilled') {
                    return {
                        id: Date.now() + index,
                        content: response.value.data[0]?.message?.content || 'No content generated',
                        loading: false
                    };
                } else {
                    return {
                        id: Date.now() + index,
                        content: `Error: ${response.reason?.message || 'Failed to generate post'}`,
                        loading: false
                    };
                }
            });

            setGeneratedPosts(updatedPosts);

            // Check if any requests failed
            const hasErrors = responses.some(response => response.status === 'rejected');
            
            if (hasErrors) {
                const allFailed = responses.every(response => response.status === 'rejected');
                if (allFailed) {
                    toastRef.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to generate posts',
                        life: 3000
                    });
                } else {
                    toastRef.current?.show({
                        severity: 'warn',
                        summary: 'Partial Success',
                        detail: 'Some posts failed to generate',
                        life: 3000
                    });
                }
            } else {
                toastRef.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Posts generated successfully',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error generating posts:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to generate posts',
                life: 3000
            });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopyToClipboard = async (content: string, postId: number) => {
        try {
            await navigator.clipboard.writeText(content);
            toastRef.current?.show({
                severity: 'success',
                summary: 'Copied',
                detail: 'Post copied to clipboard',
                life: 2000
            });
        } catch (error) {
            console.error('Failed to copy:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to copy to clipboard',
                life: 3000
            });
        }
    };

    return (
        <div className="surface-200 text-white min-h-screen p-3">
            <Toast ref={toastRef} />
            <div className="w-11 mx-auto" style={{ maxWidth: '1200px' }}>
                <div className="flex justify-content-between align-items-center mb-5">
                    <ThinkhausHeaderTitle>Social Media Post Generator</ThinkhausHeaderTitle>
                </div>

                <div className="mb-4">
                    <Card className="border-round-lg shadow-2">
                        <div className="p-fluid">
                            <div className="field mb-4">
                                <label htmlFor="model" className="block mb-2 font-bold text-xl">
                                    AI Model
                                </label>
                                <Dropdown
                                    id="model"
                                    value={selectedModel}
                                    options={modelOptions}
                                    onChange={(e) => setSelectedModel(e.value)}
                                    placeholder="Select a model"
                                    className="w-full"
                                    disabled={isGenerating}
                                />
                            </div>

                            <div className="field mb-4">
                                <label htmlFor="prompt" className="block mb-2 font-bold text-xl">
                                    Prompt
                                </label>
                                <InputTextarea
                                    id="prompt"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    rows={8}
                                    placeholder="Enter your prompt here..."
                                    className="w-full"
                                    disabled={isGenerating}
                                />
                            </div>

                            <div className="field mb-4">
                                <label htmlFor="numberOfPosts" className="block mb-2 font-bold text-xl">
                                    Number of Posts
                                </label>
                                <InputNumber
                                    data-cy="number-of-posts"
                                    value={numberOfPosts}
                                    onValueChange={(e) => setNumberOfPosts(e.value || 1)}
                                    min={1}
                                    max={10}
                                    showButtons
                                    className="w-full"
                                    disabled={isGenerating}
                                />
                            </div>

                            <Button
                                label={isGenerating ? "Thinking..." : "Generate Posts"}
                                icon="pi pi-sparkles"
                                onClick={handleSubmit}
                                loading={isGenerating}
                                loadingIcon="pi pi-spin pi-spinner"
                                disabled={isGenerating}
                                className="w-full"
                                severity="success"
                            />
                        </div>
                    </Card>
                </div>

                {generatedPosts.length > 0 && (
                    <div>
                        <h2 className="mb-3 text-2xl text-color font-bold">Generated Posts</h2>
                        <div className="grid">
                            {generatedPosts.map((post, index) => (
                                <div key={post.id} className="col-12 lg:col-6 xl:col-4">
                                    <Card
                                        className="mb-3 bg-white border-round-lg shadow-2"
                                        title={`Post ${index + 1}`}
                                        pt={{
                                            title: { className: 'text-xl font-bold text-blue-900' }
                                        }}
                                    >
                                        {post.loading ? (
                                            <div className="flex align-items-center justify-content-center p-4">
                                                <i className="pi pi-spin pi-spinner text-4xl"></i>
                                            </div>
                                        ) : (
                                            <>
                                                <div 
                                                    className="p-3 mb-3 bg-gray-100 border-round border-1 border-gray-300" 
                                                    style={{ 
                                                        whiteSpace: 'pre-wrap', 
                                                        minHeight: '150px',
                                                        lineHeight: '1.6',
                                                        color: '#333'
                                                    }}
                                                >
                                                    {post.content}
                                                </div>
                                                <Button
                                                    data-cy="copy-button"
                                                    label="Copy to Clipboard"
                                                    icon="pi pi-copy"
                                                    onClick={() => handleCopyToClipboard(post.content, post.id)}
                                                    className="w-full"
                                                    outlined
                                                />
                                            </>
                                        )}
                                    </Card>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SocialMediaPostGenerator;
